"use client";

// GA4 `scroll_depth` event (Priority 292) — percent 25/50/75/90, each at most
// once per page view. Renders nothing. Mounted only on review detail pages
// and the top three guides (see lib/scroll-depth.ts → isScrollDepthGuide).
//
// Sends only when the visitor has ALREADY accepted cookies (ff_consent=
// accepted, written by CookieConsent.tsx) and window.gtag is a function (the
// consent-default block in components/seo/AnalyticsScripts.tsx defines it).
// Otherwise it sends nothing and marks nothing as sent. It never dispatches a
// consent update and carries no measurement ID of its own.

import { useEffect } from "react";
import {
  SCROLL_DEPTH_EVENT,
  hasAcceptedCookieConsent,
  newScrollDepthThresholds,
} from "@/lib/scroll-depth";

type GtagWindow = typeof window & { gtag?: unknown };

export default function ScrollDepthTracker() {
  useEffect(() => {
    // Per mount = per page view: the tracker unmounts on navigation.
    const sent = new Set<number>();
    let frame = 0;

    const measure = () => {
      frame = 0;
      const gtag = (window as GtagWindow).gtag;
      if (typeof gtag !== "function") return;
      if (!hasAcceptedCookieConsent(document.cookie)) return;

      const doc = document.documentElement;
      // Bottom edge of the viewport as a share of the full document height.
      const percent = ((window.scrollY + window.innerHeight) / doc.scrollHeight) * 100;
      for (const threshold of newScrollDepthThresholds(percent, sent)) {
        sent.add(threshold);
        gtag("event", SCROLL_DEPTH_EVENT, { percent: threshold });
      }
    };

    // One measurement per animation frame at most, so the passive scroll
    // listener never does layout reads more often than the browser paints.
    const onScroll = () => {
      if (frame === 0) frame = window.requestAnimationFrame(measure);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, []);

  return null;
}
